import {
    AspreaderSecurity,
    AspreaderSecurityKey,
    isAsecurityKey,
    isAspreaderExchange,
    parseAsecurityVersionFromSymbol
} from '../components/security/aspreader-security';
import {Exchange, EXCHANGE_NAMING} from '../enums/exchange';
import {OPTION_TYPE_NAMING} from '../enums/option-type';
import {Security} from '../models/security';
import {SecurityKey} from '../models/security-key';
import appConfig from '../config/app-config';

export interface DisplaySymbol {
    symbol: string;
    exchange?: string;
    displayStr: string;
}

export interface SecurityNameExplained {
    symbol: string;
    expirationDateStr?: string;
    optionTypeStr?: string;
    optionTypeShortStr?: string;
    strikeStr?: string;
}

export function createDisplaySymbol(securityKey: SecurityKey, hideExchange: boolean = false): DisplaySymbol {
    const symbol: string = isAsecurityKey(securityKey)
        ? getSecurityNameExplainedForAutospreader(securityKey).symbol
        : securityKey.symbol;

    const displaySymbol: DisplaySymbol = {
        symbol,
        displayStr: symbol
    };

    if (appConfig.displayExchange && !hideExchange) {
        displaySymbol.exchange = EXCHANGE_NAMING[securityKey.securityExchange] || securityKey.securityExchange;
        displaySymbol.displayStr = `${symbol} ${displaySymbol.exchange}`;
    }

    return displaySymbol;
}

export function getSecurityNameOrExchange(security: Security): string {
    return appConfig.displayExchange
        ? EXCHANGE_NAMING[security.securityExchange] || security.securityExchange
        : security.name;
}

export function getSecurityNameExplained(security: Security | SecurityKey | undefined): SecurityNameExplained {
    let securityNameExplained: SecurityNameExplained;

    securityNameExplained = getSecurityNameExplainedForOptAndFut(security);
    securityNameExplained = securityNameExplained || getSecurityNameExplainedForAutospreader(security);

    securityNameExplained = securityNameExplained || {
        symbol: security && security.symbol || ''
    };

    return securityNameExplained;
}

/**
 * Allows to display user-friendly name of options and futures
 * @param {Security | SecurityKey} [security] while it's required to be {@link Security} to properly parse
 * Options and Futures, it also accepts {@link SecurityKey} with fallback to simple {@link Security.symbol}.
 */
// tslint:disable:cyclomatic-complexity
export function getSecurityNameExplainedForOptAndFut(
    security: Security | SecurityKey | undefined
): SecurityNameExplained {
    const enum ComplexSymbolAssetClass {
        OPT = 'OPT',
        FUT = 'FUT'
    }

    const isDeribit = security
        && security.securityExchange === Exchange.DERIBIT;

    const canParseName = isDeribit
        && (security as Security).name
        && (
            (security as Security).assetClass === ComplexSymbolAssetClass.FUT
            || (security as Security).assetClass === ComplexSymbolAssetClass.OPT
        );

    let securityNameExplained: SecurityNameExplained;

    if (canParseName) {

        // Example of security.name for Deribit exchange: BTC-23JUL21-32000-C
        const shortNameParts = (security as Security).name.split('-');
        const isOption = (security as Security).assetClass === ComplexSymbolAssetClass.OPT;
        const optionTypeShortStr = shortNameParts[3] || undefined;
        const optionType = isOption && (security as Security).optionType || undefined;

        securityNameExplained = {
            symbol:            shortNameParts[0],
            expirationDateStr: shortNameParts[1],
            optionTypeStr:     isOption && OPTION_TYPE_NAMING[optionType] || undefined,
            optionTypeShortStr,
            strikeStr:         isOption && shortNameParts[2] || undefined
        };
    }

    return securityNameExplained;
}

export function getSecurityNameExplainedForAutospreader(
    security: Security | SecurityKey | AspreaderSecurityKey | AspreaderSecurity | undefined
): SecurityNameExplained {
    const isAutospreader = !!security
        && isAspreaderExchange(security.securityExchange);

    let securityNameExplained: SecurityNameExplained;

    if (isAutospreader) {
        const verInfo = parseAsecurityVersionFromSymbol(security.symbol);

        securityNameExplained = {
            symbol: verInfo.name
        };
    }

    return securityNameExplained;
}
